// biome-ignore lint/style/noRestrictedImports: encapsulation
import type { DiffEditorProps } from "@monaco-editor/react";
import { cv } from "@suse-o11y/class-variance";
import { isString } from "@suse-o11y/is-string";
import { Logger } from "@suse-o11y/logger";
import { useUnmount } from "@suse-o11y/use-unmount";
import React, { useState } from "react";

import { SkeletonBox } from "~/components/skeleton/box";

import { DEFAULT_EDITOR_APPEARANCE, type EditorOptions, useEditorOptions } from "./_hooks/use-editor-options";
import { type EditorLanguage, LanguageLoader } from "./_languages/language-loader";
import { type HeightSpec, calculateAutoHeight } from "./_utils/calculate-auto-height";
import { getHeightProp } from "./_utils/get-height-prop";
import styles from "./_code.module.css";
import { loader } from "./_loader";
import type { Monaco } from "./_monaco";

const logger = Logger.namespace("~/components/code/diff");

const DiffEditor = React.lazy(async () => {
	// biome-ignore lint/style/noRestrictedImports: encapsulation
	const module = await import("@monaco-editor/react");

	return {
		default: module.DiffEditor,
	};
});

const variants = cv(styles.editor, {
	variants: {
		appearance: {
			text: styles.appearanceText,
			code: null,
		},
	},

	defaultVariants: {
		appearance: DEFAULT_EDITOR_APPEARANCE,
	},
});

interface DiffProps
	extends Omit<EditorOptions, "disableLinks" | "glyphMargin" | "inDiffEditor" | "quickSuggestions" | "readOnly">,
		Pick<DiffEditorProps, "className" | "width"> {
	original: string;
	modified: string;
	language: EditorLanguage;

	height?: HeightSpec | number | "auto" | (string & {});
}

const Diff = ({
	original,
	modified,
	className,
	language,
	height = "auto",
	size,
	placeholder,
	disableLineNumbers,
	disableRuler,
	disableLineHighlight,
	appearance = DEFAULT_EDITOR_APPEARANCE,
	width,
	...props
}: DiffProps) => {
	const [monaco, setMonaco] = React.useState<null | typeof Monaco>(null);
	const [contentHeight, setContentHeight] = React.useState<number | undefined>(undefined);
	const [diffEditor, setDiffEditor] = React.useState<null | Monaco.editor.IDiffEditor>(null);
	const originalEditor = diffEditor?.getOriginalEditor() ?? null;
	const modifiedEditor = diffEditor?.getModifiedEditor() ?? null;
	const [show, setShow] = useState(original.length === 0 || modified.length === 0);

	React.useEffect(() => {
		const loadMonaco = async () => {
			try {
				setMonaco(await loader());
			} catch (error) {
				logger.error("Failed to load Monaco editor:", error);
			}
		};

		void loadMonaco();
	}, []);

	/*
		Let's us hook into the lifecycle so that the DiffEditor does not show first
		without the diff regions collapsed
	*/
	React.useEffect(() => {
		if (originalEditor == null) {
			return;
		}

		const disposable = originalEditor.onDidChangeHiddenAreas(() => {
			setShow(true);
		});

		return () => {
			disposable.dispose();
		};
	}, [originalEditor]);

	const [staticHeight, dynamicHeight] = getHeightProp(height);

	const editorOptions: EditorOptions = {
		appearance,
		disableLineHighlight,
		disableLineNumbers,
		disableLinks: true,
		disableRuler,
		/*
			Adds an arrow glyph to each changed line, and a margin so that the
			left hand gutters of both sub editors are equal in size.
		*/
		glyphMargin: true,
		inDiffEditor: true,
		readOnly: true,
		placeholder,
		quickSuggestions: isString(language),
		size,
	};

	useEditorOptions(originalEditor, editorOptions);
	useEditorOptions(modifiedEditor, editorOptions);

	// Both the sub editors of DiffEditor end up with the same height after internal calculations,
	// so we can read the height of just one of them
	React.useEffect(() => {
		if (dynamicHeight == null || originalEditor == null || monaco == null) {
			return;
		}

		const updateHeight = () => {
			const subEditorHeight = calculateAutoHeight({
				monaco,
				borderWidth: 1,
				height: dynamicHeight,
				editor: originalEditor,
			});

			setContentHeight(subEditorHeight);
		};

		updateHeight();

		const disposable = originalEditor.onDidContentSizeChange(updateHeight);

		return () => {
			disposable.dispose();
		};
	}, [dynamicHeight, originalEditor, monaco]);

	/*
		Handles disposing of the diffEditor and the sub-editors, due to the props added to DiffEditor.
		Otherwise they will never be disposed!

		https://github.com/suren-atoyan/monaco-react/issues/647#issuecomment-2897027817
	*/
	useUnmount(() => () => {
		diffEditor?.dispose();

		originalEditor?.getModel()?.dispose();
		originalEditor?.dispose();

		modifiedEditor?.getModel()?.dispose();
		modifiedEditor?.dispose();
	});

	return (
		<>
			<React.Suspense fallback={<SkeletonBox className={className} />}>
				{monaco && (
					<DiffEditor
						/*
							Disable disposing within the DiffEditor component
							and handle disposing ourselves in useUnmount

							https://github.com/suren-atoyan/monaco-react/issues/647#issuecomment-2897027817
						*/
						height={contentHeight ?? staticHeight}
						keepCurrentModifiedModel={true}
						keepCurrentOriginalModel={true}
						language={isString(language) ? language : language.name}
						loading={<SkeletonBox />}
						modified={modified}
						onMount={setDiffEditor}
						options={{
							hideUnchangedRegions: {
								enabled: true,
							},
							renderGutterMenu: false,
							enableSplitViewResizing: false,
							/*
								There is a bug with wordWrap. The left sub-editor doesn't wrap unless this is set to false

								https://github.com/microsoft/monaco-editor/discussions/4454#discussioncomment-11111244
							*/
							useInlineViewWhenSpaceIsLimited: false,
						}}
						original={original}
						width={width}
						wrapperProps={{
							className: variants({
								appearance: appearance === "code" ? "code" : "text",
								// To avoid the editor loading fully and then collapsing (because of hideUnchangedRegions), we hack around it with opacity-0.
								// TODO STAC-24267 Probably can be fixed properly with the Skeleton loading fix
								className: [className, !show && "opacity-0"],
							}),
						}}
						{...props}
					/>
				)}
			</React.Suspense>

			{/*
				We have to have a LanguageLoader per editor, because the loader
				can assign some language specific properties on the editor.
			*/}
			{monaco && originalEditor && <LanguageLoader editor={originalEditor} language={language} monaco={monaco} />}

			{monaco && modifiedEditor && <LanguageLoader editor={modifiedEditor} language={language} monaco={monaco} />}
		</>
	);
};

export { Diff };
